import React, { useCallback, useMemo, useState } from 'react';
import DateTimePicker from '../../../components/DateTimePicker';
import SingleSelect from '../../../components/SingleSelect';
import { TanTable } from '../../../components/TanTable';
import service, { type InvoicingActivityGroupBy, type InvoicingActivityResponse, type InvoicingActivityRow } from '../../../services/accountingReports.service';
import { formatCurrency } from '../../../utils/money';
import { activityExportFooter, invoicingActivityColumns } from './invoicingActivityColumns';
import { calendarDate, dateParam, reportError, REPORT_BUTTON } from './reportUtils';

const GROUPS = [{ value: 'DAY', label: 'Daily' }, { value: 'WEEK', label: 'Weekly' }, { value: 'MONTH', label: 'Monthly' }];
export default function InvoicingActivityReport({ embedded = false }: { embedded?: boolean }) {
  const [start, setStart] = useState<Date | null>(() => new Date());
  const [end, setEnd] = useState<Date | null>(() => new Date());
  const [groupBy, setGroupBy] = useState<InvoicingActivityGroupBy>('DAY');
  const [result, setResult] = useState<InvoicingActivityResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const columns = useMemo(() => invoicingActivityColumns(result?.periods ?? [], result?.groupBy ?? 'DAY'), [result]);
  const exportFooter = useCallback((rows: InvoicingActivityRow[]) => activityExportFooter(result?.periods ?? [], rows), [result]);
  const Heading = embedded ? 'h2' : 'h1';
  const run = async () => {
    if (!start || !end) { setError('Enter both a From Date and a To Date.'); return; }
    if (dateParam(start) > dateParam(end)) { setError('End date must be on or after start date.'); return; }
    setLoading(true); setError(null); setResult(null);
    try { setResult(await service.getInvoicingActivity({ start: dateParam(start), end: dateParam(end), groupby: groupBy })); }
    catch (e) { setError(reportError(e, 'Unable to load Invoicing Activity. Please try again.')); }
    finally { setLoading(false); }
  };
  return <div className={`${embedded ? '' : 'p-4 '}space-y-4 text-label`}>
    <Heading className="text-xl font-semibold">Invoicing Activity</Heading>
    <div className="rounded-lg border border-theme-border-primary bg-theme-bg-primary p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl">
        <DateTimePicker mode="date" labelType="custom" label="From Date" ariaLabel="From Date" required value={start} onChange={v => setStart(calendarDate(v))} disabled={loading} />
        <DateTimePicker mode="date" labelType="custom" label="To Date" ariaLabel="To Date" required value={end} onChange={v => setEnd(calendarDate(v))} disabled={loading} />
        <div><div className="text-sm mb-1">Group By</div><SingleSelect ariaLabel="Group By" options={GROUPS} value={groupBy} onChange={v => setGroupBy(v as InvoicingActivityGroupBy)} disabled={loading} /></div>
      </div>
      <button onClick={run} className={REPORT_BUTTON} disabled={loading}>{loading ? 'Running…' : 'Run Report'}</button>
      <p className="text-sm text-muted">Activity is based on invoice creation date. PRO counts include each invoice record, matching V1.</p>
    </div>
    {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
    {result && <>
      <p className="text-sm font-medium">{result.criteria}</p>
      <p className="font-semibold">Grand Total: {result.grandTotal.pronumbers.toLocaleString()} PROs · {result.grandTotal.invoices.toLocaleString()} invoices · {formatCurrency(result.grandTotal.amount)}</p>
      {result.rows.length === 0 && <p role="status" className="text-sm text-muted">No invoicing activity found for this date range.</p>}
    </>}
    {(loading || (result && result.rows.length > 0)) && <div className="h-[65vh] min-h-80">
      <TanTable<InvoicingActivityRow> data={result?.rows ?? []} columns={columns} loading={loading} getRowId={row => row.username}
        settingsKey={`accounting-invoicing-activity-${result?.groupBy ?? groupBy}`} cardTitle="Invoicing Activity"
        enableGrouping={false} showColumnFooter exportFooter={exportFooter} fillContainer enableColumnFilters />
    </div>}
  </div>;
}
